import { MDSIconKind } from "@/mds/components/icon/icon/icon.types";
import type { AppStore } from "@/store";
import type { OnboardingContext, OnboardingStepDefinition } from "./onboarding-types";

const isProactiveTrialOnboardingApplicable = (ctx: OnboardingContext): boolean => {
  if (ctx.onboardingFlowVersion === "intent_v1") {
    return false;
  }
  if (ctx.hasCompletedSharedSignupOnboarding) {
    return false;
  }

  if (!ctx.isEligibleForProactiveTrialOnboarding) {
    return false;
  }

  if (ctx.proactiveTrialOnboardingDebugOverride) {
    return true;
  }

  if (!ctx.isProactiveTrialEligibilityAuthoritative || !ctx.isProactiveTrialEligible) {
    return false;
  }

  if (ctx.proactiveTrialOnboardingServerProgress === "completed") {
    return false;
  }

  return (
    ctx.proactiveTrialOnboardingServerProgress === "pending" ||
    ctx.proactiveTrialOnboardingIsRelevant
  );
};

const isProactiveTrialOnboardingCompleted = (ctx: OnboardingContext): boolean => {
  if (ctx.hasCompletedSharedSignupOnboarding) {
    return true;
  }

  if (ctx.hasProactiveAccess || ctx.hasCompletedOrSkippedProactiveTrialOnboarding) {
    return true;
  }

  return (
    !ctx.proactiveTrialOnboardingDebugOverride &&
    ctx.proactiveTrialOnboardingServerProgress === "completed"
  );
};

const isMemAgentIntroOnboardingCompleted = (ctx: OnboardingContext): boolean =>
  ctx.hasCompletedMemAgentIntroOnboarding || isProactiveTrialOnboardingCompleted(ctx);

/** Completes calendar setup and disables scheduled meeting reminders when setup ended with no calendar. */
const completeCalendarSetup = (store: AppStore) => {
  store.calendarOnboarding.setPhase("completed");

  if (store.calendar.google.isConnected || store.calendar.outlook.isConnected) {
    return;
  }

  store.systemPermissions.setCalendarNotificationEnabled({ enabled: false });
};

/**
 * The ordered onboarding step pipeline.
 *
 * Each step declares:
 *  - `applicableWhen` — whether this step should appear at all
 *  - `completedWhen` — whether the step has been completed/dismissed
 *  - `substeps` — declarative substeps with activeWhen predicates
 *  - `complete` — the action to run when the user completes or skips this step
 *
 * Steps with internal component navigation (voice-permissions → restart-if-required,
 * meeting-intro → meeting-permissions, import app screens) keep their
 * navigation inside the component. The substeps here only describe the
 * store-visible entry points.
 */
export const ONBOARDING_STEP_DEFINITIONS: OnboardingStepDefinition[] = [
  // Intent is account-shared and must be saved before the web-to-native handoff.
  {
    id: "intent-picker",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion === "intent_v1" && !ctx.intentOnboardingIsCompleted,
    completedWhen: ctx => ctx.onboardingIntent !== null,
    substeps: [{ id: "intent-picker", activeWhen: () => true }],
    complete: () => undefined,
  },

  // Native app download (web browsers on Mac/Win/iOS only)
  {
    id: "native-app-download",
    // New-user eligible accounts only see this step as the account-shared
    // blocker; existing accounts keep the old local dismissible upsell.
    applicableWhen: ctx => {
      if (ctx.onboardingFlowVersion === "intent_v1") {
        return (
          !ctx.intentOnboardingIsCompleted &&
          ctx.onboardingIntent !== null &&
          ctx.shouldShowNativeAppDownload
        );
      }

      return (
        !ctx.hasCompletedSharedSignupOnboarding &&
        ctx.shouldShowNativeAppDownload &&
        (ctx.hasPendingNativeAppDownloadBlockingOnboarding ||
          (!ctx.isEligibleForProactiveTrialOnboarding &&
            !ctx.hasCompletedOrSkippedNativeAppDownload))
      );
    },
    completedWhen: ctx => {
      if (ctx.onboardingFlowVersion === "intent_v1") {
        return ctx.intentOnboardingIsCompleted;
      }
      if (ctx.hasCompletedSharedSignupOnboarding) {
        return true;
      }

      if (ctx.hasPendingNativeAppDownloadBlockingOnboarding) {
        return false;
      }

      if (ctx.isEligibleForProactiveTrialOnboarding) {
        return true;
      }

      return ctx.hasCompletedOrSkippedNativeAppDownload;
    },
    substeps: [{ id: "native-app-download", activeWhen: () => true }],
    complete: (store, _completedSteps, ctx) => {
      if (
        ctx?.hasPendingNativeAppDownloadBlockingOnboarding === true ||
        ctx?.isEligibleForProactiveTrialOnboarding === true
      ) {
        return;
      }

      store.platformInfo.perDeviceOnboardingInfo.completeOrSkipNativeAppDownload();
    },
    debugReset: store => store.platformInfo.perDeviceOnboardingInfo.debugResetNativeAppDownload(),
    debugApplicableLabel: ctx => `shouldShowNativeAppDownload: ${ctx.shouldShowNativeAppDownload}`,
    debugCompletedLabel: ctx =>
      `hasPendingNativeAppDownloadBlockingOnboarding: ${ctx.hasPendingNativeAppDownloadBlockingOnboarding}, isEligibleForProactiveTrialOnboarding: ${ctx.isEligibleForProactiveTrialOnboarding}, hasCompletedOrSkippedNativeAppDownload: ${ctx.hasCompletedOrSkippedNativeAppDownload}`,
  },

  // Legacy ICP RFI stays in its established position.
  {
    id: "icp-rfi",
    applicableWhen: ctx => ctx.onboardingFlowVersion !== "intent_v1",
    completedWhen: ctx => ctx.hasCompletedSharedSignupOnboarding || !ctx.shouldShowIcpRfi,
    substeps: [{ id: "referrer", activeWhen: () => true }],
    complete: (store, completedSteps) =>
      store.platformInfo.onboardingInfo.completeIcpRfiStep(completedSteps ?? []),
    debugReset: store => store.platformInfo.onboardingInfo.debugReset(),
    debugApplicableLabel: () => "always applicable",
    debugCompletedLabel: ctx => `shouldShowIcpRfi: ${ctx.shouldShowIcpRfi}`,
  },

  // 3. Mem Agent intro — new onboarding users meet Mem Agent before setup pages
  {
    id: "mem-agent-intro",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion !== "intent_v1" && isProactiveTrialOnboardingApplicable(ctx),
    completedWhen: isMemAgentIntroOnboardingCompleted,
    substeps: [{ id: "mem-agent-intro", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.onboardingInfo.completeOnboardingTasks(["mem-agent-intro"]),
    debugApplicableLabel: ctx =>
      `isEligibleForProactiveTrialOnboarding: ${ctx.isEligibleForProactiveTrialOnboarding}, isProactiveTrialEligibilityAuthoritative: ${ctx.isProactiveTrialEligibilityAuthoritative}, isProactiveTrialEligible: ${ctx.isProactiveTrialEligible}, proactiveTrialOnboardingServerProgress: ${ctx.proactiveTrialOnboardingServerProgress}, proactiveTrialOnboardingIsRelevant: ${ctx.proactiveTrialOnboardingIsRelevant}`,
    debugCompletedLabel: ctx =>
      `hasCompletedMemAgentIntroOnboarding: ${ctx.hasCompletedMemAgentIntroOnboarding}`,
  },

  // 4. Existing-user Mem Agent intro — legacy devices that completed onboarding before this screen existed
  {
    id: "existing-user-mem-agent-intro",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion !== "intent_v1" &&
      ctx.isMemAgentAccessEnabled &&
      ctx.existingUserMemAgentOnboardingIsRelevant,
    completedWhen: ctx => ctx.hasCompletedExistingUserMemAgentIntroOnboarding,
    substeps: [{ id: "existing-user-mem-agent-intro", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.onboardingInfo.completeOnboardingTasks(["existing-user-mem-agent-intro"]),
    debugReset: store =>
      store.platformInfo.perDeviceOnboardingInfo.debugResetExistingUserMemAgentOnboarding(),
    debugApplicableLabel: ctx =>
      `isMemAgentAccessEnabled: ${ctx.isMemAgentAccessEnabled}, existingUserMemAgentOnboardingIsRelevant: ${ctx.existingUserMemAgentOnboardingIsRelevant}`,
    debugCompletedLabel: ctx =>
      `hasCompletedExistingUserMemAgentIntroOnboarding: ${ctx.hasCompletedExistingUserMemAgentIntroOnboarding}`,
  },

  // 5. Calendar setup — calendar-connect / calendar-select with declarative substep resolution (always applicable)
  {
    id: "calendar-setup",
    stepperIcon: MDSIconKind.Calendar,
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion !== "intent_v1" || ctx.onboardingIntent === "meetings",
    completedWhen: ctx =>
      ctx.intentOnboardingIsCompleted ||
      ctx.hasCompletedSharedSignupOnboarding ||
      !ctx.shouldShowCalendarOnboardingSetup,
    substeps: [
      {
        id: "calendar-connect",
        activeWhen: ctx => !ctx.hasPendingCalendarConnection,
        debugActiveLabel: ctx =>
          `hasPendingCalendarConnection: ${ctx.hasPendingCalendarConnection}`,
      },
      {
        id: "calendar-select",
        activeWhen: ctx => ctx.hasPendingCalendarConnection,
        debugActiveLabel: ctx =>
          `hasPendingCalendarConnection: ${ctx.hasPendingCalendarConnection}`,
      },
    ],
    complete: completeCalendarSetup,
    debugReset: store => store.calendarOnboarding.debugReset(),
    debugApplicableLabel: () => "always applicable",
    debugCompletedLabel: ctx =>
      `shouldShowCalendarOnboardingSetup: ${ctx.shouldShowCalendarOnboardingSetup}`,
  },

  // 6. Meeting permissions (Apple Silicon Mac desktop only) — intro is component-internal
  {
    id: "meeting-permissions",
    stepperIcon: MDSIconKind.Bell,
    applicableWhen: ctx =>
      ctx.isAppleSiliconMacDesktop &&
      (ctx.onboardingFlowVersion !== "intent_v1" || ctx.onboardingIntent === "meetings"),
    completedWhen: ctx =>
      ctx.intentOnboardingIsCompleted ||
      ctx.hasCompletedSharedSignupOnboarding ||
      !ctx.shouldShowMeetingOnboardingSystemPermissions,
    substeps: [{ id: "system-permissions-meeting-permissions", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.perDeviceOnboardingInfo.dismissMeetingOnboardingSystemPermissions(),
    debugReset: store => store.platformInfo.perDeviceOnboardingInfo.debugResetMeetingPermissions(),
    debugApplicableLabel: ctx => `isAppleSiliconMacDesktop: ${ctx.isAppleSiliconMacDesktop}`,
    debugCompletedLabel: ctx =>
      `shouldShowMeetingOnboardingSystemPermissions: ${ctx.shouldShowMeetingOnboardingSystemPermissions}`,
  },

  // 7. Voice permissions (Apple Silicon Mac desktop only) — restart-if-required is component-internal
  {
    id: "voice-permissions",
    stepperIcon: MDSIconKind.Microphone,
    applicableWhen: ctx =>
      ctx.isAppleSiliconMacDesktop &&
      (ctx.onboardingFlowVersion !== "intent_v1" || ctx.onboardingIntent === "meetings"),
    completedWhen: ctx =>
      ctx.intentOnboardingIsCompleted ||
      ctx.hasCompletedSharedSignupOnboarding ||
      !ctx.shouldShowVoiceOnboardingSystemPermissions,
    substeps: [{ id: "system-permissions-voice-permissions", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.perDeviceOnboardingInfo.dismissVoiceOnboardingSystemPermissions(),
    debugReset: store => store.platformInfo.perDeviceOnboardingInfo.debugResetVoicePermissions(),
    debugApplicableLabel: ctx => `isAppleSiliconMacDesktop: ${ctx.isAppleSiliconMacDesktop}`,
    debugCompletedLabel: ctx =>
      `shouldShowVoiceOnboardingSystemPermissions: ${ctx.shouldShowVoiceOnboardingSystemPermissions}`,
  },

  {
    id: "intent-quick-thought-permissions",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion === "intent_v1" &&
      ctx.onboardingIntent === "quick_thoughts" &&
      (ctx.isAppleSiliconMacDesktop || ctx.isWindowsDesktop),
    completedWhen: ctx =>
      ctx.intentOnboardingIsCompleted || !ctx.shouldShowVoiceOnboardingSystemPermissions,
    substeps: [{ id: "intent-quick-thought-permissions", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.perDeviceOnboardingInfo.dismissVoiceOnboardingSystemPermissions(),
  },

  {
    id: "intent-quick-thought-capture",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion === "intent_v1" &&
      ctx.onboardingIntent === "quick_thoughts" &&
      ctx.isDesktop,
    completedWhen: ctx =>
      ctx.intentOnboardingIsCompleted ||
      ctx.quickThoughtsCaptureMethod === "typed" ||
      ctx.hasCompletedMemAgentFirstMessageOnboarding,
    substeps: [{ id: "intent-quick-thought-capture", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.perDeviceOnboardingInfo.completeMemAgentFirstMessageOnboarding(),
  },

  {
    id: "intent-proactive-trial",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion === "intent_v1" &&
      ctx.onboardingIntent !== null &&
      !ctx.intentOnboardingIsCompleted,
    completedWhen: ctx =>
      ctx.intentOnboardingIsCompleted ||
      ctx.hasProactiveAccess ||
      ctx.hasCompletedOrSkippedProactiveTrialOnboarding,
    substeps: [{ id: "intent-proactive-trial", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding(),
  },

  // 8. Proactive trial — new onboarding users who do not already have Proactive access
  {
    id: "proactive-trial",
    applicableWhen: isProactiveTrialOnboardingApplicable,
    completedWhen: isProactiveTrialOnboardingCompleted,
    substeps: [{ id: "proactive-trial", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding(),
    debugReset: store =>
      store.platformInfo.perDeviceOnboardingInfo.debugResetProactiveTrialOnboarding(),
    debugApplicableLabel: ctx =>
      `isEligibleForProactiveTrialOnboarding: ${ctx.isEligibleForProactiveTrialOnboarding}, isProactiveTrialEligibilityAuthoritative: ${ctx.isProactiveTrialEligibilityAuthoritative}, isProactiveTrialEligible: ${ctx.isProactiveTrialEligible}, proactiveTrialOnboardingServerProgress: ${ctx.proactiveTrialOnboardingServerProgress}, proactiveTrialOnboardingIsRelevant: ${ctx.proactiveTrialOnboardingIsRelevant}`,
    debugCompletedLabel: ctx =>
      `hasCompletedOrSkippedProactiveTrialOnboarding: ${ctx.hasCompletedOrSkippedProactiveTrialOnboarding}, proactiveTrialOnboardingServerProgress: ${ctx.proactiveTrialOnboardingServerProgress}, hasProactiveAccess: ${ctx.hasProactiveAccess}`,
  },

  {
    id: "intent-referrer",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion === "intent_v1" &&
      ctx.onboardingIntent !== null &&
      !ctx.intentOnboardingIsCompleted,
    completedWhen: ctx => ctx.intentOnboardingIsCompleted,
    substeps: [{ id: "intent-referrer", activeWhen: () => true }],
    // The intent completion endpoint also sets the legacy iOS dismissal
    // receipt. Keeping this atomic prevents another client from reopening an
    // onboarding flow between two requests.
    complete: store => store.platformInfo.completeIntentOnboarding(),
  },

  // 9. Mem Agent shortcut — desktop-only push-to-talk setup after the Proactive trial decision
  {
    id: "mem-agent-shortcut",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion !== "intent_v1" &&
      ctx.isDesktop &&
      ctx.isFloatingAgentOnboardingSurfaceAvailable &&
      ctx.memAgentOnboardingIsRelevant,
    completedWhen: ctx => ctx.hasCompletedMemAgentShortcutOnboarding,
    substeps: [{ id: "mem-agent-shortcut", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.perDeviceOnboardingInfo.completeMemAgentShortcutOnboarding(),
    debugReset: store => store.platformInfo.perDeviceOnboardingInfo.debugResetMemAgentOnboarding(),
    debugApplicableLabel: ctx =>
      `isDesktop: ${ctx.isDesktop}, isFloatingAgentOnboardingSurfaceAvailable: ${ctx.isFloatingAgentOnboardingSurfaceAvailable}, memAgentOnboardingIsRelevant: ${ctx.memAgentOnboardingIsRelevant}`,
    debugCompletedLabel: ctx =>
      `hasCompletedMemAgentShortcutOnboarding: ${ctx.hasCompletedMemAgentShortcutOnboarding}`,
  },

  // 10. Web Mem Agent prompt — desktop uses the shortcut step instead
  {
    id: "mem-agent-first-message",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion !== "intent_v1" &&
      !ctx.isDesktop &&
      ctx.isFloatingAgentOnboardingSurfaceAvailable &&
      ctx.memAgentOnboardingIsRelevant,
    completedWhen: ctx => ctx.hasCompletedMemAgentFirstMessageOnboarding,
    substeps: [{ id: "mem-agent-first-message", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.perDeviceOnboardingInfo.completeMemAgentFirstMessageOnboarding(),
    debugReset: store => store.platformInfo.perDeviceOnboardingInfo.debugResetMemAgentOnboarding(),
    debugApplicableLabel: ctx =>
      `isFloatingAgentOnboardingSurfaceAvailable: ${ctx.isFloatingAgentOnboardingSurfaceAvailable}, memAgentOnboardingIsRelevant: ${ctx.memAgentOnboardingIsRelevant}`,
    debugCompletedLabel: ctx =>
      `hasCompletedMemAgentFirstMessageOnboarding: ${ctx.hasCompletedMemAgentFirstMessageOnboarding}`,
  },

  // 11. Existing-user Proactive trial — legacy users see the offer after the Agent intro
  {
    id: "existing-user-proactive-trial",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion !== "intent_v1" &&
      ctx.isMemAgentAccessEnabled &&
      ctx.existingUserMemAgentOnboardingIsRelevant,
    completedWhen: ctx =>
      ctx.hasProactiveAccess || ctx.hasCompletedExistingUserProactiveTrialOnboarding,
    substeps: [{ id: "existing-user-proactive-trial", activeWhen: () => true }],
    complete: store => {
      store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding();
      return store.platformInfo.onboardingInfo.completeOnboardingTasks([
        "existing-user-proactive-trial",
      ]);
    },
    debugApplicableLabel: ctx =>
      `isMemAgentAccessEnabled: ${ctx.isMemAgentAccessEnabled}, existingUserMemAgentOnboardingIsRelevant: ${ctx.existingUserMemAgentOnboardingIsRelevant}`,
    debugCompletedLabel: ctx =>
      `hasCompletedExistingUserProactiveTrialOnboarding: ${ctx.hasCompletedExistingUserProactiveTrialOnboarding}, hasProactiveAccess: ${ctx.hasProactiveAccess}`,
  },

  // 12. Existing-user Mem Agent first message — desktop-only push-to-remember setup
  {
    id: "existing-user-mem-agent-first-message",
    applicableWhen: ctx =>
      ctx.onboardingFlowVersion !== "intent_v1" &&
      ctx.isDesktop &&
      ctx.isMemAgentAccessEnabled &&
      ctx.existingUserMemAgentOnboardingIsRelevant,
    completedWhen: ctx => ctx.hasCompletedExistingUserMemAgentFirstMessageOnboarding,
    substeps: [{ id: "existing-user-mem-agent-first-message", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.onboardingInfo.completeOnboardingTasks([
        "existing-user-mem-agent-first-message",
      ]),
    debugReset: store =>
      store.platformInfo.perDeviceOnboardingInfo.debugResetExistingUserMemAgentOnboarding(),
    debugApplicableLabel: ctx =>
      `isDesktop: ${ctx.isDesktop}, isMemAgentAccessEnabled: ${ctx.isMemAgentAccessEnabled}, existingUserMemAgentOnboardingIsRelevant: ${ctx.existingUserMemAgentOnboardingIsRelevant}`,
    debugCompletedLabel: ctx =>
      `hasCompletedExistingUserMemAgentFirstMessageOnboarding: ${ctx.hasCompletedExistingUserMemAgentFirstMessageOnboarding}`,
  },
];
